import { Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { CreateNewScrubbingService } from './create-new-scrubbing.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { SubmissionService } from '../../submission-data/submission.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-create-new-scrubbing',
    templateUrl: './create-new-scrubbing.component.html',
    styleUrls: ['./create-new-scrubbing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreateNewScrubbingComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  /**
   * Optional row seed for the in-process Add/Edit modal hosted by
   * `EfileScrubbingComponent`. When provided it overrides the row read
   * from the workspace's active screen — letting the scrubbing list tab
   * open the modal pre-filled with the row the user clicked, without
   * mutating workspace state. Pass `null` (default) for "Add" mode.
   */
  @Input() rowSeed: any = null;

  /**
   * Host-supplied context from the CE host dialog flow
   * (`CeHostDialogComponent` calls `setAttribute('row-data', JSON…)` /
   * `setAttribute('filter-params', JSON…)` on the registered CE
   * `gtw-efile-create-new-scrubbing-ce`). Required because custom-grid
   * efile screens — which open this CE via
   * `efile-grid.directive`'s `autoOpenCeDialog` flow — typically have an
   * empty `WorkspaceService.activeScreen`, so the legacy
   * `firstGridRowObjectFromPayload` / `normalizeFilterParams` fallbacks
   * return nothing. Mirrors the contract on
   * `xml-vs-elements.component.ts`.
   *
   * Pattern A (`EfileScrubbingComponent` inline modal) is unaffected:
   * `rowSeed` continues to win over both host attributes and the active
   * screen.
   */
  private hostFilterParams: any = {};
  private hostRowData: any = {};

  @Input('filter-params')
  set _filter_params(value: any) {
    this.hostFilterParams = this.parseBoundContext(value);
    this.applyWorkspaceContext();
  }

  @Input('row-data')
  set _row_data(value: any) {
    this.hostRowData = this.parseBoundContext(value);
    this.applyWorkspaceContext();
  }

  private subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: ''
  };
  headerData: any = {};
  globalService: any = {};
  rowData: any = {};
  filterParams: any = {};

  label = '';
  searchKey = '';
  replaceWith = '';
  isEdit = false;
  scrubbingId = 0;
  minimise = false;
  loading = false;

  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private createNewScrubbingService: CreateNewScrubbingService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private submissionService: SubmissionService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-seed the form when a host (EfileScrubbingComponent) swaps the
    // rowSeed between Add and Edit invocations of the same instance.
    if (changes['rowSeed'] && !changes['rowSeed'].firstChange) {
      this.applyWorkspaceContext();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.headerData = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = this.headerData.userSettingsData;
    this.SERVER_CONFIG = this.headerData.serverConfigData;
    this.GENERAL_CONFIG = this.headerData.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);

    // Merge filter context: active-screen filters first (workspace path),
    // host-supplied attributes second so an explicit CE host dialog
    // payload wins over stale or empty tab filters. Same merge order as
    // `xml-vs-elements.component.ts` for parity.
    const normalized = normalizeFilterParams(activeScreen);
    this.filterParams = { ...normalized, ...(this.hostFilterParams || {}) };

    // Row precedence (most explicit → most general):
    //   1. `rowSeed` (Pattern A inline modal — `EfileScrubbingComponent`).
    //   2. `hostRowData` from the CE host dialog (Pattern B — efile-grid
    //      directive `autoOpenCeDialog` flow on custom-grid screens
    //      where `activeScreen` is empty).
    //   3. The workspace's active-screen row payload (Pattern B legacy
    //      deep-link / pencil-icon flow).
    const rowFromHost =
      this.hostRowData && Object.keys(this.hostRowData || {}).length > 0
        ? this.hostRowData
        : null;
    const rowFromActive = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
    this.rowData =
      this.rowSeed && Object.keys(this.rowSeed || {}).length > 0
        ? this.rowSeed
        : rowFromHost || rowFromActive;

    this.tryInit();
    this.cdr.markForCheck();
  }

  /**
   * Parse a CE attribute payload. Accepts JSON-encoded strings (`setAttribute(
   * 'row-data', JSON.stringify(...))` paths from {@link CeHostDialogComponent})
   * and plain objects (Angular property binding). Returns `{}` when the
   * input is unparseable so callers can still spread it safely.
   */
  private parseBoundContext(val: any): any {
    if (val == null || val === '') {
      return {};
    }
    if (typeof val === 'object' && !Array.isArray(val)) {
      return { ...val };
    }
    const s = String(val).trim();
    if (!s || s === '[object Object]') {
      return {};
    }
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const first = parsed[0];
        return typeof first === 'object' && first !== null ? { ...first } : {};
      }
      return typeof parsed === 'object' && parsed !== null ? { ...parsed } : {};
    } catch {
      return {};
    }
  }

  /**
   * Resolve a single screen-context parameter at save time by scanning
   * every source the legacy `JsonObjectFactory.saveJSON` flow can
   * populate, in priority order:
   *
   *   1. `this.filterParams`          — active-screen filters + hostFilterParams
   *                                     merged in `applyWorkspaceContext`
   *   2. `this.hostFilterParams`      — raw CE attribute `filter-params`
   *                                     (set by `CeHostDialogComponent` /
   *                                     legacy AngularJS `vm.filterParams | json`)
   *   3. `this.rowData`               — `rowSeed` / hostRowData / active row
   *                                     (already resolved by `applyWorkspaceContext`)
   *   4. `this.hostRowData`           — raw CE attribute `row-data`
   *   5. live `WorkspaceService.activeScreen.filters` (re-read at save time)
   *   6. live `WorkspaceService.activeScreen` row payload
   *   7. `SubmissionService.activeSubmission` — direct port of legacy
   *      `EfileFactory.selectedSubmission`. The legacy create-new-scrubbing
   *      controller pulled `SUBMISSION_ID` and `TAX_YEAR` straight off
   *      this object; preserving it here recovers those fields whenever
   *      the launching screen has an open submission tab but doesn't
   *      forward them through `filter-params` / `row-data` (the typical
   *      Pattern-B custom-grid flow).
   *   8. `this.globalService`         — `hydratedGlobalParams` snapshot
   *   9. `GlobalService.global.globalParams`
   *
   * The first non-empty value wins. Both `snake_case` and `UPPER_CASE`
   * casings are accepted — legacy routes mostly use lower-snake but
   * `data.selected.rowData` columns arrive `UPPER_CASE`. Mirrors the
   * resolution discipline used by
   * `xml-vs-elements.component.ts.resolveIrsId`.
   */
  private resolveScreenParam(keys: string[]): string {
    const liveActive = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const liveFilters = normalizeFilterParams(liveActive);
    const liveRow = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(liveActive));
    const activeSubmission = this.submissionService?.activeSubmission as any;
    const globalParams = (this.gtwGlobal as any)?.global?.globalParams || {};

    const sources: any[] = [
      this.filterParams,
      this.hostFilterParams,
      this.rowData,
      this.hostRowData,
      liveFilters,
      liveRow,
      activeSubmission,
      this.globalService,
      globalParams,
    ];

    const variants = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      variants.add(k);
      variants.add(k.toUpperCase());
      variants.add(k.toLowerCase());
    }

    for (const src of sources) {
      if (!src || typeof src !== 'object') {
        continue;
      }
      for (const k of variants) {
        const v = (src as any)[k];
        if (v != null) {
          const s = String(v).trim();
          if (s !== '' && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'undefined') {
            return s;
          }
        }
      }
    }
    return '';
  }

  /**
   * Resolve the custom-screen envelope (`activity_key`, `screen_key`,
   * `jcd_ta_key`, `screen_type`, `jcd_key`) at save time.
   *
   * Required because custom-grid efile screens that open this CE via
   * `efile-grid.directive`'s `autoOpenCeDialog` flow typically have an
   * empty `WorkspaceService.activeScreen`, so `hydratedGlobalParams`
   * (which reads from active-screen + filterParams only at component
   * init) misses these keys. By re-resolving from
   * `hostFilterParams`/`hostRowData` first we recover the values the
   * launching screen propagated through the CE attributes.
   *
   * `screen_type` defaults to `'custom'` (matching the legacy efile
   * custom-screen contract) when no source carries it.
   */
  private resolveCustomScreenContext(): Record<string, string> {
    const screenType = this.resolveScreenParam(['screen_type']);
    return {
      activity_key: this.resolveScreenParam(['activity_key']),
      screen_key: this.resolveScreenParam(['screen_key']),
      jcd_ta_key: this.resolveScreenParam(['jcd_ta_key']),
      screen_type: screenType || 'custom',
      jcd_key: this.resolveScreenParam(['jcd_key']),
    };
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    const hasBaseUrl = (this.baseURLs?.custom || this.baseURLs?.api || this.GENERAL_CONFIG?.base_url);
    if (!hasBaseUrl) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    const row = this.rowData;
    if (row && (row.LABEL || row.SEARCH_KEY || row.REPLACE_KEY || row.SCRUBBING_ID)) {
      this.label = row.LABEL || '';
      this.searchKey = row.SEARCH_KEY || '';
      this.replaceWith = row.REPLACE_KEY || '';
      this.scrubbingId = Number(row.SCRUBBING_ID) || 0;
      this.isEdit = true;
    } else if (row && Object.keys(row).length > 0) {
      this.scrubbingId = 0;
      this.isEdit = false;
    }
    this.cdr.detectChanges();
  }

  save(): void {
    const baseUrl = (this.GENERAL_CONFIG?.base_url) || this.baseURLs.custom || this.baseURLs.api;
    if (!baseUrl) {
      this.gtwSnackbarService.openSnackBar('Base URL not configured.', 'gtw-snackbar--red');
      return;
    }

    // ------------------------------------------------------------------
    // Resolve every save-time parameter through the same multi-source
    // scan used for the custom-screen envelope below. Pre-existing
    // ad-hoc fallbacks (`rowData ?? filterParams ?? globalService ??
    // <default>`) only inspected one variant of each casing, which
    // misses values that arrive via the CE host dialog attributes
    // (`hostFilterParams` / `hostRowData`) — the typical Pattern-B
    // flow on custom-grid screens whose `WorkspaceService.activeScreen`
    // is empty when this modal opens.
    //
    // `resolveScreenParam` mirrors `XmlVsElementsComponent.resolveIrsId`:
    // it walks `filterParams` → `hostFilterParams` → `rowData` →
    // `hostRowData` → live workspace filters/row → `globalService` →
    // `globalParams`, accepting `snake_case`/`UPPER_CASE`/`camelCase`
    // variants so a single call covers every legacy spelling.
    // ------------------------------------------------------------------
    const submissionId = this.resolveScreenParam(['submission_id']);
    const taxYear = this.resolveScreenParam(['tax_year']);
    const resolvedScenario = this.resolveScreenParam(['scenario']);
    const scenario: string | number = resolvedScenario || 74;
    const ssoId =
      this.USER_SETTINGS?.user?.sso_id ??
      this.resolveScreenParam(['sso_id']);
    const resolvedJcdKey = this.resolveScreenParam(['jcd_key']);
    const jcdKey: string | number = resolvedJcdKey || 250;
    const scenarioCode =
      this.resolveScreenParam(['scenarioCode', 'scenario_code']) || 'RAF';
    const isIssueKeyEnabled =
      this.resolveScreenParam(['is_issue_key_enabled']) || 'N';

    // Custom-screen envelope — populated by `efile-grid.directive` when
    // launching this CE from a custom-grid host (it folds these keys
    // into both `gridExtraParams` → `filter-params` and
    // `headerData.globalParams`). Resolved here so the save payload is
    // resilient when the launching screen is the only source.
    const customCtx = this.resolveCustomScreenContext();

    this.loading = true;
    this.cdr.detectChanges();

    const params: { [key: string]: string | number } = {
      scrubbing_id: this.scrubbingId,
      submission_id: submissionId,
      search_word: this.searchKey,
      replace_word: this.replaceWith,
      label: this.label,
      tax_year: taxYear,
      scenario,
      jcd_key: jcdKey,
      sso_id: ssoId,
      issue_id: this.resolveScreenParam(['issue_id']),
      issue_short_desc: this.resolveScreenParam(['issue_short_desc']),
      checkLockedbj: JSON.stringify([{ scenario_key: String(scenario) }]),
      scenarioCode,
      is_issue_key_enabled: isIssueKeyEnabled,
      activity_key: customCtx.activity_key,
      screen_key: customCtx.screen_key,
      jcd_ta_key: customCtx.jcd_ta_key,
      screen_type: customCtx.screen_type,
    };

    this.createNewScrubbingService.saveScrubbing(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.loading = false;
        this.isLoading = false;
        const callSuccess = response?.callSuccess;
        if (callSuccess === '0') {
          this.gtwSnackbarService.openSnackBar('Something went wrong while saving the data!', 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Data saved successfully!', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.loading = false;
        this.isLoading = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong while saving the data!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.cdr.detectChanges();
  }
}
